import React, { useContext, useState } from 'react';
import t from 'react-translate';
import { useSelector, useDispatch } from 'react-redux';
import uuid from 'react-uuid';

// redux
import { getFlatCourseAliases } from 'redux/selectors/course';
import {
  createAiLearnerGoal,
  deleteAiLearnerGoal,
} from 'redux/actions/ai-role-play';

import { useLecturePageParams } from 'lecture_pages/hooks/lecture-routing';
import {
  ReactComponent as AIObjectives,
} from 'styles/icons/ai-objectives.svg';
import { doubleSpacing } from 'styles/global_defaults/scaffolding';
import NvTextArea from 'shared/components/inputs/nv-text-area';
import { useFieldArray, useFormContext } from 'react-hook-form';
import ObjectiveInputList from './objective-input-list';
import EditAiInstructionContext from './context';

type GoalField = {
  // Internal key used by React Hook Form's useFieldArray for tracking/reconciliation
  fieldId: string;
  // Real database ID (number) for existing items, or uuid (string) for newly created items in create mode
  id: number | string;
  text: string;
};

const LearnerObjectiveSetting = () => {
  const params = useLecturePageParams();
  const [isAddingNewLearnerGoal, setIsAddingNewLearnerGoal] = useState(false);

  const courseAliases = useSelector(state => getFlatCourseAliases(state, params.catalogId));
  const {
    control,
    register,
    setValue,
    getValues,
  } = useFormContext();
  const { aiRolePlayId } = useContext(EditAiInstructionContext) || {};
  const dispatch = useDispatch();

  const {
    fields,
    append,
  } = useFieldArray({
    name: 'aiLearnerGoals',
    control,
    keyName: 'fieldId',
  });

  // We mutate control._defaultValues directly because RHF v7.22.1 doesn't support
  // shouldDirty: false on field array methods (append, remove), (added in v7.36.0). Public alternatives
  // like resetField reset both value and default (wiping pending text edits), and
  // reset(getValues(), { keepDirtyValues: true }) requires v7.31.0+. This is a
  // pragmatic tradeoff: it re-baselines defaults after already-saved structural
  // changes so isDirty reflects only unsaved text edits. Should be replaced with
  // native shouldDirty: false on append/remove if RHF is upgraded to v7.36.0+.
  const updateGoalsDefaultValue = () => {
    const currentGoals = getValues('aiLearnerGoals') || [];
    // eslint-disable-next-line no-underscore-dangle
    control._defaultValues.aiLearnerGoals = currentGoals.map((obj) => ({ ...obj }));
  };

  const handleAddAiLearnerGoal = () => {
    if (aiRolePlayId) {
      setIsAddingNewLearnerGoal(true);
      dispatch(createAiLearnerGoal({
        catalogId: params.catalogId,
        aiRolePlayId,
        text: '',
      })).then((res) => {
        if (res) {
          // Use setValue instead of append to avoid marking the field as dirty,
          // since the goal is already saved to the backend
          const current = getValues('aiLearnerGoals') || [];
          setValue('aiLearnerGoals', [...current, { id: res.payload.id, text: '' }], { shouldDirty: false });
          updateGoalsDefaultValue();
        }
      }).finally(() => {
        setIsAddingNewLearnerGoal(false);
      });
    } else {
      append({ id: uuid(), text: '' });
    }
  };

  const handleRemoveAiLearnerGoal = (aiLearnerGoalId) => {
    if (fields.length <= 0) return;
    // Use setValue instead of remove to avoid marking the field as dirty,
    // since the deletion is already saved to the backend
    const current = getValues('aiLearnerGoals') || [];
    setValue(
      'aiLearnerGoals',
      current.filter((field) => field.id !== aiLearnerGoalId),
      { shouldDirty: false },
    );
    updateGoalsDefaultValue();
    if (aiRolePlayId && aiLearnerGoalId) {
      dispatch(deleteAiLearnerGoal({
        catalogId: params.catalogId,
        aiRolePlayId,
        aiLearnerGoalId,
      }));
    }
  };

  return (
    <div>
      <div className='d-flex flex-column'>
        <div className='d-flex justify-content-center mt-8'>
          <AIObjectives
            height={doubleSpacing}
            width={doubleSpacing}
          />
        </div>
        <p className='page-title mb-4 mt-2 mx-auto text-black'>
          {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.THE_LEARNER_GOALS(courseAliases)}
        </p>
        <p className='text-align-justify text-large-body lh-25'>
          {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.WRITE_LEARNER_OBJECTIVES(courseAliases)}
        </p>
      </div>
      {/* Hidden input to register the id field for required learner goal */}
      <input type='hidden' {...register('requiredAiLearnerGoal.id')} />
      <NvTextArea
        name='requiredAiLearnerGoal.text'
        withForm
        className='border-gray-5 bg-white required-textarea'
        textareaClassName='min-h-sm text-large-body lh-25'
        required
      />
      <ObjectiveInputList
        name='aiLearnerGoals'
        fields={fields}
        handleAddField={handleAddAiLearnerGoal}
        handleRemoveField={handleRemoveAiLearnerGoal}
        isAddingField={isAddingNewLearnerGoal}
        maxFieldCount={4}
      />
    </div>
  );
};

export default LearnerObjectiveSetting;
